const formatCurrency = (amount, locale = "en-US", currency = "USD") => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency,
  }).format(amount);
};

const formatRate = (rateValue) => {
  if (!rateValue) return "";

  const num = parseFloat(rateValue);
  if (isNaN(num)) return "";

  // Preserve precision up to 3 decimal digits, trim trailing zeros
  let formatted = num.toString();

  // If the original value was a string and included %, don't add it
  const hasPercent = typeof rateValue === 'string' && rateValue.includes('%');
  return `${formatted}${hasPercent ? '' : '%'}`;
};

const formatPhoneValue = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "");

  // split into main (10 digits) and ext (max 4)
  const main = digits.slice(0, 10);
  const ext = digits.length > 10 ? digits.slice(10, 14) : ""; // limit to 4

  // format main
  let mainFormatted = main;
  if (main.length <= 3) {
    mainFormatted = main;
  } else if (main.length <= 6) {
    mainFormatted = `${main.slice(0, 3)}-${main.slice(3)}`;
  } else {
    mainFormatted = `${main.slice(0, 3)}-${main.slice(3, 6)}-${main.slice(6, 10)}`;
  }

  return ext ? `${mainFormatted} ${ext}` : mainFormatted;
};


const phoneOnInput = (e) => {
  // preserve caret could be complex — keep simple: reformat on input
  const formatted = formatPhoneValue(e.target.value || "");
  e.target.value = formatted;
};

const phoneOnBlur = (e) => {
  // final cleanup on blur (also ensure exactly 10 digits main if present)
  const formatted = formatPhoneValue(e.target.value || "");
  e.target.value = formatted;
};

const formatSSNValue = (raw) => {
  if (!raw) return "";
  const digits = raw.replace(/\D/g, "").slice(0, 9); // max 9 digits

  if (digits.length <= 3) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

const ssnOnInput = (e) => {
  e.target.value = formatSSNValue(e.target.value);
};

const ssnOnBlur = (e) => {
  e.target.value = formatSSNValue(e.target.value);
};

// --- Currency input formatting (commas while typing, .00 on blur) ---

const addThousandsSeparators = (digits) => {
  if (!digits) return "";
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Format currency while typing:
 * - keeps commas in integer part
 * - preserves decimal part as typed (no forced 2 decimals here)
 * - allows only one dot
 */
const formatCurrencyTyping = (raw) => {
  if (raw === null || raw === undefined) return "";

  // keep digits and dot only
  let s = String(raw).replace(/[^\d.]/g, "");

  // allow only first dot
  const firstDot = s.indexOf(".");
  if (firstDot !== -1) {
    s =
      s.slice(0, firstDot + 1) +
      s.slice(firstDot + 1).replace(/\./g, "");
  }

  const [intPartRaw, decPartRaw = ""] = s.split(".");

  // remove leading zeros but keep single 0 if needed
  const intPart = intPartRaw.replace(/^0+(?=\d)/, "");
  const intFormatted = addThousandsSeparators(intPart);

  // keep decimal as-is while typing (limit to 2 digits if you want)
  const decPart = decPartRaw.slice(0, 2);

  if (firstDot !== -1) {
    return `${intFormatted || "0"}.${decPart}`;
  }
  return intFormatted;
};

/**
 * Final format on blur:
 * - adds commas
 * - forces exactly 2 decimals
 */
const formatCurrencyBlur = (raw) => {
  if (raw === null || raw === undefined) return "";

  const cleaned = String(raw).replace(/[^\d.]/g, "");
  if (!cleaned) return "";

  const num = Number(cleaned);
  if (!Number.isFinite(num)) return "";

  // format with commas and 2 decimals (no currency symbol)
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const currencyOnInput = (e) => {
  e.target.value = formatCurrencyTyping(e.target.value);
};

const currencyOnBlur = (e) => {
  e.target.value = formatCurrencyBlur(e.target.value);
};

/**
 * Ensures currency input can NEVER be negative
 * - strips minus signs (including unicode)
 * - clamps value to >= 0
 * - returns formatted value (string)
 */
const enforceNonNegativeCurrency = (raw, onBlur = false) => {
  if (raw === null || raw === undefined) return "";

  // remove minus signs (normal + unicode)
  const noMinus = String(raw).replace(/[−–—-]/g, "");

  // keep only digits and dot (leave as string while typing)
  const cleaned = noMinus.replace(/[^\d.]/g, "");

  if (onBlur) {
    const num = Number(cleaned);
    if (!Number.isFinite(num) || num <= 0) return "";
    return formatCurrencyBlur(num);
  }

  // IMPORTANT: do NOT convert to Number while typing
  // this preserves "100." and "100.50"
  if (!cleaned) return "";
  const numCheck = Number(cleaned);
  if (!Number.isFinite(numCheck) || numCheck <= 0) return "";

  return formatCurrencyTyping(cleaned);
};

export {
  formatCurrency,
  formatRate,
  phoneOnInput,
  phoneOnBlur,
  ssnOnInput,
  ssnOnBlur,
  currencyOnInput,
  currencyOnBlur,
  formatCurrencyTyping,
  formatCurrencyBlur,
  enforceNonNegativeCurrency
};

